import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { PassOfferSummary } from '../../models/Pass';
import { PaymentMethod } from '../../models/Organization';
import type { TutorSessionType } from '../../models/Tutor';
import { userApiClient } from '../../userApiClient';
import { authService } from '../../services/AuthService';
import { setPassPurchaseResume } from '../../utils/passPurchaseResume';
import { formatPassPrice, passEntitlementLabel } from '../../utils/passFormatting';
import { formatPassTargetSummary } from '../../utils/passTargets';
import { getPassCheckoutErrorCode, PassCheckoutErrorCode } from '../../utils/passCheckoutErrors';
import {
  defaultPassCheckoutPaymentMethod,
  resolvePassCheckoutPaymentMethods,
} from '../../utils/passPurchaseAvailability';
import TutorLessonSessionTypeChip from '../tutor/TutorLessonSessionTypeChip';
import SpaceTermsAcceptance from '../booking-steps/SpaceTermsAcceptance';

interface PassPurchaseConfirmDialogProps {
  open: boolean;
  offer: PassOfferSummary | null;
  onClose: () => void;
  /** Org/tutor/room payment methods that gate CASH vs ONLINE in checkout. */
  supportedPaymentMethods?: PaymentMethod[] | null;
  /** Display names for the offer's target collection (order matches `targetIds`). */
  targetNames?: (string | null | undefined)[] | null;
  sessionType?: TutorSessionType | null;
  /**
   * Organization T&C (text or URL). When non-empty, buyer must accept before checkout
   * (or before the login redirect that resumes checkout).
   */
  regulation?: string | null;
  /** Optional label for the T&C link (e.g. space name). Defaults to generic "T&C". */
  termsName?: string | null;
}

/**
 * Confirms a pass purchase and starts checkout (Stripe online or cash pending).
 * Anonymous visitors are redirected to /login with the purchase stashed via `passPurchaseResume`.
 */
const PassPurchaseConfirmDialog: React.FC<PassPurchaseConfirmDialogProps> = ({
  open,
  offer,
  onClose,
  supportedPaymentMethods,
  targetNames,
  sessionType,
  regulation,
  termsName,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<PassCheckoutErrorCode | null>(null);
  const [regulationAccepted, setRegulationAccepted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(undefined);

  const checkoutMethods = useMemo(
    () => resolvePassCheckoutPaymentMethods(supportedPaymentMethods),
    [supportedPaymentMethods],
  );

  const trimmedRegulation = regulation?.trim() || '';
  const hasRegulation = Boolean(trimmedRegulation);
  const canBuy =
    Boolean(offer) && Boolean(paymentMethod) && (!hasRegulation || regulationAccepted);

  useEffect(() => {
    if (open) {
      setErrorCode(null);
      setSubmitting(false);
      setRegulationAccepted(false);
      setPaymentMethod(defaultPassCheckoutPaymentMethod(supportedPaymentMethods));
    }
  }, [open, offer, trimmedRegulation, supportedPaymentMethods]);

  const resolvedTargetNames = useMemo(() => {
    const names = (targetNames ?? [])
      .map((name) => name?.trim())
      .filter((name): name is string => Boolean(name));
    return names.length > 0 ? names : null;
  }, [targetNames]);

  const resolvedTargetLabel = useMemo(() => {
    if (!resolvedTargetNames) return null;
    return formatPassTargetSummary(resolvedTargetNames);
  }, [resolvedTargetNames]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleBuy = async () => {
    if (!offer || !canBuy || !paymentMethod) return;
    setSubmitting(true);
    setErrorCode(null);

    if (!authService.getIsLoggedIn()) {
      const returnPath = `${location.pathname}${location.search}`;
      setPassPurchaseResume({
        passTemplateId: offer.id,
        returnPath,
        paymentMethod,
      });
      onClose();
      navigate('/login', { state: { redirectUrl: returnPath } });
      return;
    }

    try {
      const response = await userApiClient.checkoutPass({
        passTemplateId: offer.id,
        paymentMethod,
      });
      if (paymentMethod === PaymentMethod.CASH) {
        onClose();
        navigate('/passes/purchase/pending');
        return;
      }
      if (!response.stripeCheckoutUrl) {
        throw new Error('Missing Stripe checkout URL in response');
      }
      window.location.href = response.stripeCheckoutUrl;
    } catch (err) {
      console.error('Error starting pass checkout:', err);
      setErrorCode(getPassCheckoutErrorCode(err));
      setSubmitting(false);
    }
  };

  const errorMessage =
    errorCode === 'connectNotReady'
      ? t(
          'passes.checkoutConnectNotReady',
          'This pass is temporarily unavailable for online purchase. The organization has not finished setting up checkout.',
        )
      : errorCode === 'generic'
        ? t('passes.checkoutError', 'Failed to start checkout. Please try again.')
        : null;

  const EntitlementIcon =
    offer?.unitType === 'COUNT' ? MenuBookRoundedIcon : AccessTimeRoundedIcon;

  const checkoutHint =
    paymentMethod === PaymentMethod.CASH
      ? t(
          'passes.confirmCashCheckoutHint',
          'You’ll pay on site. The host will activate your pass when payment is collected.',
        )
      : t(
          'passes.confirmCheckoutHint',
          'You’ll complete payment on a secure Stripe checkout page.',
        );

  const buyLabel =
    paymentMethod === PaymentMethod.CASH
      ? t('passes.buyCash', 'Request cash purchase')
      : t('passes.buy', 'Buy');

  const submittingLabel =
    paymentMethod === PaymentMethod.CASH
      ? t('passes.buyingCash', 'Submitting…')
      : t('passes.buying', 'Redirecting…');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="pass-purchase-dialog-title"
    >
      <DialogTitle sx={{ pt: 2.5, px: { xs: 2.25, sm: 3 }, pb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={(theme) => ({
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.14),
              color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
              flexShrink: 0,
            })}
          >
            <ConfirmationNumberRoundedIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.35 }}>
            <Typography
              id="pass-purchase-dialog-title"
              variant="h6"
              component="h2"
              sx={{ fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em' }}
            >
              {t('passes.confirmTitle', 'Buy pass')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              {t(
                'passes.confirmSubtitle',
                'Review the offer, then continue to secure checkout.',
              )}
            </Typography>
          </Box>
          <IconButton
            aria-label={t('common.close', 'Close')}
            onClick={handleClose}
            disabled={submitting}
            edge="end"
            size="small"
            sx={{ mt: 0.15 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2.25, sm: 3 }, pt: 1, pb: 1.5 }}>
        {offer ? (
          <Stack spacing={2}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                    {offer.title}
                  </Typography>
                  {resolvedTargetLabel ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t('passes.forTarget', 'For {{name}}', { name: resolvedTargetLabel })}
                    </Typography>
                  ) : null}
                  {sessionType ? (
                    <Box sx={{ mt: 1 }}>
                      <TutorLessonSessionTypeChip sessionType={sessionType} />
                    </Box>
                  ) : null}
                </Box>

                <Divider />

                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EntitlementIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">{passEntitlementLabel(offer, t)}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EventAvailableRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {offer.validityStartPolicy === 'FROM_FIRST_USE'
                        ? t(
                            'passes.validForDaysFromFirstUse',
                            'Valid for {{count}} days from first use',
                            { count: offer.validityDurationDays },
                          )
                        : t('passes.validForDays', 'Valid for {{count}} days from purchase', {
                            count: offer.validityDurationDays,
                          })}
                    </Typography>
                  </Stack>
                </Stack>

                <Box
                  sx={{
                    mt: 0.5,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
                    {t('passes.total', 'Total')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                    {formatPassPrice(offer, i18n.language)}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {checkoutMethods.length > 1 ? (
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>
                  {t('passes.paymentMethod', 'Payment method')}
                </Typography>
                <RadioGroup
                  value={paymentMethod ?? ''}
                  onChange={(event) => setPaymentMethod(event.target.value as PaymentMethod)}
                >
                  {checkoutMethods.map((method) => (
                    <FormControlLabel
                      key={method}
                      value={method}
                      control={<Radio size="small" />}
                      label={t(`paymentMethod.${method.toLowerCase()}`, method)}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            ) : null}

            {hasRegulation ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <SpaceTermsAcceptance
                  regulation={trimmedRegulation}
                  spaceName={termsName?.trim() || resolvedTargetLabel || undefined}
                  accepted={regulationAccepted}
                  onAcceptedChange={setRegulationAccepted}
                />
              </Box>
            ) : null}

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{ textAlign: 'center' }}
            >
              <LockOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary">
                {checkoutHint}
              </Typography>
            </Stack>

            {errorMessage ? (
              <Alert severity={errorCode === 'connectNotReady' ? 'warning' : 'error'}>
                {errorMessage}
              </Alert>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.25, sm: 3 },
          pb: { xs: 2.25, sm: 3 },
          pt: 1,
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
          '& > :not(style) ~ :not(style)': { ml: 0 },
        }}
      >
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleBuy}
          disabled={submitting || !canBuy}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {submitting ? submittingLabel : buyLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PassPurchaseConfirmDialog;
